import {useEffect, useState} from "react";
import {campaignCountdown, msUntilCountdownChanges, type CampaignCountdown} from "./campaignCountdown";

/**
 * A live countdown to the campaign's deadline (kitecorp/transferify-ui#1448, api
 * kitecorp/transferify-api-service#933).
 *
 * Thin adapter, like every hook here: it holds a clock and schedules the next tick. What the remaining
 * time IS, and whether there is any honest deadline to show at all, are decided by the React-free rules
 * in `./campaignCountdown`.
 *
 * <b>It ticks on the next minute BOUNDARY, not on a fixed interval.</b> A one-second timer re-renders
 * sixty times for every visible change; a one-minute timer drifts until the displayed minute is up to
 * fifty-nine seconds stale. `setTimeout` re-armed per tick rather than `setInterval` for the same
 * reason — the delay is different every time, and the first one is almost never a whole minute.
 *
 * <b>Null is a real answer, and the common one.</b> No `endsOn` means an always-on campaign, which has
 * no deadline; a passed deadline means the offer is over. Both render no timer rather than "0d 0h",
 * and the caller must treat null as "show the offer without a countdown" rather than "hide the offer" —
 * an uncapped campaign is still a live discount.
 *
 * @param endsOn the campaign's last open day as the api sends it, or null/undefined for always-on
 */
export function useCampaignCountdown(endsOn: string | null | undefined): CampaignCountdown | null {
	const [countdown, setCountdown] = useState(() => campaignCountdown(endsOn, Date.now()));

	useEffect(() => {
		// Recomputed on mount and whenever the deadline changes, not only on the timer: the first render
		// used the clock at initialiser time, which for a component mounted before the api answered is a
		// deadline that did not exist yet.
		const current = campaignCountdown(endsOn, Date.now());
		setCountdown(current);
		if (current === null) {
			return;
		}
		let timer: ReturnType<typeof setTimeout>;
		const tick = () => {
			const next = campaignCountdown(endsOn, Date.now());
			setCountdown(next);
			if (next !== null) {
				timer = setTimeout(tick, msUntilCountdownChanges(next));
			}
			// A settled countdown arms nothing further. Without this the timer would keep firing against
			// a deadline that has passed, re-rendering a component that now shows nothing, forever.
		};
		timer = setTimeout(tick, msUntilCountdownChanges(current));
		return () => clearTimeout(timer);
	}, [endsOn]);

	return countdown;
}
